import { ComplianceCaseTypeEnum } from "#graphql/supergraphEnums";
import {
  type AccountTypeEnum,
  type ComplianceObjectType,
  type DataEntryStatus,
  type ProfileRelationshipEnum,
  type DocumentStatus,
  type AmlEntityTypeQuestion,
  type ProfileType,
  type RequestedDocumentType,
} from "#graphql/supergraphTypes";
import { Programs } from "#utils/compliance_program";

export enum ShowDetailsKey {
  YES = "yes",
  NO = "no",
  SURVEY_COUNT = "surveyCount",
  AML_ENTITY_ID = "amlEntityId",
}

export enum CompletionDetailsKey {
  DATE_OF_BIRTH = "dateOfBirth",
  LEGAL_NAME = "legalName",
  AML_ENTITY_TYPE_ID = "amlEntityTypeId",
}

export enum CompletionDocumentStatus {
  COMPLETED = "completed",
  ACKNOWLEDGED = "acknowledged",
  EXPIRED = "expired",
  NOT_STARTED = "notStarted",
}

export enum DocumentsStatus {
  REQUIRED = "required",
  NOT_REQUIRED = "notRequired",
}

export enum OwnersKey {
  YES = "yes",
  NO = "no",
  RELATIONSHIP_CRITERIA = "relationshipCriteria",
  JOINT_MIN_TWO_CHILDREN = "jointMinTwoChildren",
}

export enum ControllersKey {
  YES = "yes",
  NO = "no",
  RELATIONSHIP_CRITERIA = "relationshipCriteria",
  RELATIONSHIP_CRITERIA_AML = "relationshipCriteriaAML",
}

export enum ProgramType {
  KYC = "kyc",
  AML = "aml",
}

export enum ProfileCategory {
  INDIVIDUAL = "individual",
  JOINT_INDIVIDUAL = "jointIndividual",
  JOINT = "joint",
  ENTITY = "entity",
}

export type ParentRelationshipForStatus = Pick<ProfileType["parentRelationships"][0], "id"> & {
  relationshipType: ProfileRelationshipEnum;
};

export type RequestedDocumentTypeForStatus = Pick<RequestedDocumentType, "isMandatory"> & {
  baseType: string;
  subType?: string | null;
  status: DocumentStatus;
};

export type DataEntryStatusForStatus = Pick<
  DataEntryStatus,
  "ownersCompleted" | "controllersCompleted" | "documentsCompleted"
>;

export type DocumentForStatus = {
  documentPurpose?: {
    baseType: string;
  } | null;
  metadata?: Array<{
    key: string;
    value: string;
  }> | null;
  expiryDate?: string | null;
};

export type RuleEngineStatus = {
  documentTypes: RequestedDocumentTypeForStatus[];
};

export type RootProfileConfig = {
  id: number;
  accountType?: AccountTypeEnum | null;
  rulesEngine?: RuleEngineStatus | null;
  amlEntityTypeQuestions?: AmlEntityTypeQuestion[] | null;
};

// Flexible type that accepts profile data from GraphQL queries
export type ProfileTypeForStatus = {
  id: number;
  complianceObjectType: ComplianceObjectType | null;
  accountType?: AccountTypeEnum | null;
  dateOfBirth?: string | null;
  legalName?: string | null;
  amlEntityTypeId?: number | null;
  caseDataEntryStatus?: DataEntryStatusForStatus | null;
  parentRelationships?: ParentRelationshipForStatus[] | null;
  rulesEngine?: RuleEngineStatus | null;
  documents?: DocumentForStatus[] | null;
  /** Count of direct child profiles linked as Owner (used for joint min-two-children rule). */
  childOwnersCount?: number;
};

export type StatusKeys = {
  detailsKey: ShowDetailsKey;
  detailsCompletionKey: CompletionDetailsKey[] | null;
  documentsStatus: boolean;
  ownersKey: OwnersKey;
  controllersKey: ControllersKey;
};

export type ProfileDisplayStatus = {
  detailsAvailability: boolean;
  documentsAvailability: boolean;
  ownerAvailability: boolean;
  controllerAvailability: boolean;
};

export type ProfileCompletionStatus = {
  detailsCompletion: boolean | null;
  documentsCompletionStatus: CompletionDocumentStatus | null;
  ownersCompletionStatus: boolean | null;
  controllersCompletionStatus: boolean | null;
};

export type RuleEntry = {
  context: ComplianceCaseTypeEnum;
  programType: Programs[];
  profileCategory: ProfileCategory;
  isRoot: boolean;
};
